import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Icon } from '@mui/material';
import classNames from 'classnames';
import { loader } from 'graphql.macro';
import React, { useEffect, useRef, useState } from 'react';
import { Text } from '../typography';
import NotificationCard from './NotificationCard';
import styles from './NotificationList.module.scss';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { Spinner, Form } from 'react-bootstrap';

const notifierQuery = loader('./NotificationList.graphql');
const updateNotifierMutation = loader('./NotificationList.update.graphql');
const markAllNotifiersMutation = loader('./NotificationList.markAll.graphql');

const pageSize = 50;

export default function NotificationList({
  show,
  onHide,
  refetchCount,
  containerRef,
  expand = false,
}) {
  const [selectAll, setSelectAll] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectCards, setSelectCards] = useState(false);
  const [showFullText, setShowFullText] = useState(expand);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [notifiers, setNotifiers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const client = useApolloClient();
  // Ref so the data effect below always calls the latest prop without
  // re-running when the parent passes a new function each render
  const refetchCountRef = useRef(refetchCount);
  refetchCountRef.current = refetchCount;
  const { t } = useTranslation();

  const { refetch, loading, data } = useQuery(notifierQuery, {
    skip: !show,
    variables: {
      page: 1,
      pageSize,
      unreadOnly,
    },
    fetchPolicy: 'no-cache',
  });

  // Synced from data rather than onCompleted, which doesn't reliably fire on
  // refetch - also resets any pages loaded with "show more"
  useEffect(() => {
    if (!data) return;
    const firstPage = data.notifiers ?? [];
    setNotifiers(firstPage);
    setHasMore(firstPage.length === pageSize);
    refetchCountRef.current?.();
  }, [data]);

  const loadMore = () => {
    const lastId = notifiers[notifiers.length - 1]?.id;
    if (!lastId || loadingMore) return;
    setLoadingMore(true);
    client
      .query({
        query: notifierQuery,
        variables: { page: 1, pageSize, unreadOnly, beforeId: lastId },
        fetchPolicy: 'no-cache',
      })
      .then(({ data: { notifiers: nextPage = [] } = {} }) => {
        setNotifiers((prev) => [...prev, ...nextPage]);
        setHasMore(nextPage.length === pageSize);
      })
      .finally(() => setLoadingMore(false));
  };

  const [updateNotifier] = useMutation(updateNotifierMutation);
  const [markAllNotifiers] = useMutation(markAllNotifiersMutation);

  const resetSelection = () => {
    setSelectAll(false);
    setSelectCards(false);
    setSelected([]);
  };

  // option is the field to be updated ie. isArchived or seen
  const updateNotifiers = (selected, option) => {
    // Select all applies server side so notifiers that aren't loaded
    // (past the page or hidden) are included
    const mutation = selectAll
      ? markAllNotifiers({ variables: { [option]: true } })
      : updateNotifier({
          variables: { notifierIds: selected, [option]: true },
        });
    mutation.then(() => {
      refetch();
      refetchCount?.();
    });
    resetSelection();
  };

  const closeNotificationList = () => {
    onHide();
    resetSelection();
  };

  useEffect(() => {
    if (!show) {
      resetSelection();
    }
  }, [show]);

  return show ? (
    <div
      ref={containerRef}
      className={classNames(
        styles.container,
        show ? styles.show : styles.hide,
        showFullText ? styles.extendedContainer : styles.collapsedContainer,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <div className={styles.header}>
          <div className={styles.options}>
            <Text
              className={classNames(
                styles.option,
                notifiers?.length ? styles.enabled : '',
                selectCards && styles.activated,
              )}
              size="sm"
              weight="semiBold"
              noMargin
              onClick={() => notifiers?.length && setSelectCards(!selectCards)}
            >
              {t(keys.action.SELECT)}
            </Text>
            <Text
              className={classNames(
                styles.option,
                selectCards ? styles.enabled : '',
              )}
              size="sm"
              weight="semiBold"
              noMargin
              onClick={() => selectCards && updateNotifiers(selected, 'seen')}
            >
              {t(keys.notifications.MARK_AS_READ)}
            </Text>
            <Text
              className={classNames(
                styles.option,
                selectCards ? styles.enabled : '',
              )}
              size="sm"
              weight="semiBold"
              noMargin
              onClick={() =>
                selectCards && updateNotifiers(selected, 'isArchived')
              }
            >
              {t(keys.action.DELETE)}
            </Text>
          </div>

          <div className={styles.buttonContainer}>
            <Icon
              onClick={() => setShowFullText(!showFullText)}
              fontSize="0.5rem"
            >
              {showFullText ? 'expand_less' : 'expand_more'}
            </Icon>
            <Icon
              fontSize="inherit"
              baseClassName="material-icons-outlined"
              className={styles.closeButton}
              onClick={closeNotificationList}
            >
              close
            </Icon>
          </div>
        </div>
        <div className={styles.filterRow}>
          <Form.Check
            type="switch"
            id="notification-unread-only"
            checked={unreadOnly}
            onChange={() => {
              resetSelection();
              setUnreadOnly(!unreadOnly);
            }}
          />
          <Text noMargin weight="semiBold" color="secondary" size="sm">
            {t(keys.notifications.UNREAD_ONLY)}
          </Text>
        </div>
        {selectCards && (
          <div className={styles.selectAll}>
            <Form.Check
              checked={selectAll}
              value={selectAll}
              onChange={(e) => {
                e.stopPropagation();
                if (selectAll) {
                  setSelected([]);
                } else {
                  setSelected(notifiers.map(({ id }) => id));
                }
                setSelectAll(!selectAll);
              }}
            />
            <Text noMargin weight="semiBold" color="secondary" size="sm">
              {t(keys.notifications.SELECT_ALL)}
            </Text>
          </div>
        )}
        {selectCards && selectAll && (
          <Text
            className={styles.selectAllHint}
            noMargin
            color="secondary"
            size="sm"
          >
            {t(keys.notifications.SELECT_ALL_HINT)}
          </Text>
        )}
      </div>
      <div
        className={classNames(
          styles.notificationContainer,
          showFullText
            ? styles.extendedNotificationContainer
            : styles.collapsedNotificationContainer,
        )}
      >
        {notifiers?.length ? (
          <>
            {notifiers
              .filter((n) => n)
              .map((notifier) => {
                return (
                  <NotificationCard
                    onClick={() => {
                      updateNotifiers([notifier?.id], 'seen');
                      closeNotificationList();
                    }}
                    key={notifier.id}
                    notifier={notifier}
                    showFullText={showFullText}
                    selectActivated={selectCards}
                    onSelected={() => {
                      if (selected?.includes(notifier?.id)) {
                        // Deselecting a card drops back to only the
                        // selected ids
                        setSelectAll(false);
                        setSelected(selected.filter((n) => n !== notifier.id));
                      } else {
                        setSelected([...selected, notifier?.id]);
                      }
                    }}
                    selected={selected.includes(notifier?.id)}
                  />
                );
              })}
            {hasMore && (
              <div className={styles.footer}>
                {loadingMore ? (
                  <Spinner animation="border" variant="primary" size="sm" />
                ) : (
                  <Text
                    className={styles.moreButton}
                    noMargin
                    weight="semiBold"
                    color="accentPrimary"
                    size="sm"
                    onClick={loadMore}
                  >
                    {t(keys.common.SHOW_MORE)}
                  </Text>
                )}
              </div>
            )}
          </>
        ) : loading ? (
          <div className={styles.secondaryContainer}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className={styles.secondaryContainer}>
            <Text noMargin weight="semiBold" color="secondary">
              {t(keys.action.NOT_FOUND, {
                variable: t(keys.notifications.NOTIFICATIONS),
              })}
            </Text>
          </div>
        )}
      </div>
    </div>
  ) : null;
}
